import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  type RunnerDisplayResponse,
  type RunnerDisplayStatus,
  type RunnerHosting,
} from "./api";
import { isRunnerNotFound, runnerErrorText } from "./errors";
import { RetireRunnerModal } from "./RetireRunnerModal";
import { RunnerTokenModal } from "./RunnerTokenModal";
import { useRegisterRunner } from "./useRegisterRunner";
import { useReissueRunnerToken } from "./useReissueRunnerToken";
import { useRunners } from "./useRunners";
import { useMyPermissions } from "../permissions/useMyPermissions";

// Место исполнения — зеркало backend-enum `RunnerHosting`.
function hostingLabelKey(hosting: RunnerHosting): string {
  return hosting === "self_hosted"
    ? "runners.hosting.selfHosted"
    : "runners.hosting.saas";
}

// Цвет бейджа отображаемого статуса (composite liveness + занятость,
// `modules/runner/liveness.py::derive_display_status`). busy/idle —
// на связи, остальное — non-online liveness.
function statusColor(status: RunnerDisplayStatus): string {
  if (status === "busy") {
    return "blue";
  }
  if (status === "idle") {
    return "green";
  }
  if (status === "offline") {
    return "yellow";
  }
  return "gray";
}

function statusLabelKey(status: RunnerDisplayStatus): string {
  if (status === "busy") {
    return "runners.status.busy";
  }
  if (status === "idle") {
    return "runners.status.idle";
  }
  if (status === "offline") {
    return "runners.status.offline";
  }
  if (status === "stale") {
    return "runners.status.stale";
  }
  return "runners.status.neverContacted";
}

// Экран «Настройки → Раннеры» (ADR-0073): регистрация self-hosted
// раннера организации (форма «Имя раннера»), таблица раннеров с
// выведенным статусом и местом исполнения, перевыпуск токена с
// подтверждением, снятие с учёта с подтверждением (`RetireRunnerModal`;
// кнопка скрыта, если набор прав точно не содержит `runner.manage`, —
// пока набор неизвестен, она показана, `403` обрабатывается реактивно).
// Токен из ответа регистрации/перевыпуска показывается через
// `RunnerTokenModal` один раз; `onClose` модалки сбрасывает и
// `shownToken`, и мутации (`registerRunner.reset()`/
// `reissueToken.reset()`) — токен не попадает в query cache, но без
// `reset()` оставался бы в `data` завершённой мутации дольше, чем нужно.
export function RunnersSection(): ReactElement {
  const { t } = useTranslation();
  const runners = useRunners();
  const registerRunner = useRegisterRunner();
  const reissueToken = useReissueRunnerToken();
  const canManage = !useMyPermissions().isDenied("runner.manage");

  const [name, setName] = useState("");
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [shownToken, setShownToken] = useState<string | null>(null);
  const [reissueTarget, setReissueTarget] =
    useState<RunnerDisplayResponse | null>(null);
  const [reissueError, setReissueError] = useState<string | null>(null);
  const [retireTarget, setRetireTarget] =
    useState<RunnerDisplayResponse | null>(null);

  // Пустое имя блокирует сам `disabled` кнопки (ниже) — здесь
  // дополнительный guard был бы недостижимым кодом (кнопка не кликабельна).
  const handleRegister = (): void => {
    const trimmed = name.trim();
    setRegisterError(null);
    registerRunner.mutate(trimmed, {
      onSuccess: (result) => {
        setName("");
        setShownToken(result.runner_token);
      },
      onError: (err) => {
        setRegisterError(runnerErrorText(err, t));
      },
    });
  };

  const openReissueConfirm = (runner: RunnerDisplayResponse): void => {
    setReissueError(null);
    setReissueTarget(runner);
  };

  const cancelReissue = (): void => {
    setReissueTarget(null);
    setReissueError(null);
  };

  return (
    <Stack gap="xl">
      <Text size="sm">{t("runners.intro")}</Text>

      <Stack gap="sm">
        <Title order={2}>{t("runners.registerTitle")}</Title>
        {registerError !== null ? (
          <Alert color="red" title={t("runners.errorHeading")}>
            {registerError}
          </Alert>
        ) : null}
        <Group align="flex-end" gap="sm">
          <TextInput
            label={t("runners.nameLabel")}
            placeholder={t("runners.namePlaceholder")}
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
          <Button
            onClick={handleRegister}
            disabled={name.trim() === ""}
            loading={registerRunner.isPending}
          >
            {t("runners.registerButton")}
          </Button>
        </Group>
      </Stack>

      <Stack gap="sm">
        <Title order={2}>{t("runners.listTitle")}</Title>
        {runners.isPending ? (
          <Loader />
        ) : runners.isError ? (
          <Alert color="red" title={t("runners.errorHeading")}>
            {t("runners.loadingError")}
          </Alert>
        ) : runners.data.length === 0 ? (
          <Text c="dimmed">{t("runners.empty")}</Text>
        ) : (
          <Table withTableBorder withColumnBorders striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("runners.colName")}</Table.Th>
                <Table.Th>{t("runners.colPlace")}</Table.Th>
                <Table.Th>{t("runners.colStatus")}</Table.Th>
                <Table.Th>{t("runners.colActions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {runners.data.map((runner) => (
                <Table.Tr key={runner.runner_id}>
                  <Table.Td>{runner.name ?? runner.runner_id}</Table.Td>
                  <Table.Td>{t(hostingLabelKey(runner.hosting))}</Table.Td>
                  <Table.Td>
                    <Badge color={statusColor(runner.display_status)}>
                      {t(statusLabelKey(runner.display_status))}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => {
                          openReissueConfirm(runner);
                        }}
                      >
                        {t("runners.reissueButton")}
                      </Button>
                      {canManage ? (
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          onClick={() => {
                            setRetireTarget(runner);
                          }}
                        >
                          {t("runners.retireButton")}
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <Modal
        opened={reissueTarget !== null}
        onClose={cancelReissue}
        title={t("runners.reissue.confirmTitle")}
        centered
      >
        {reissueTarget !== null ? (
          <Stack gap="md">
            <Text>
              {t("runners.reissue.confirmText", {
                name: reissueTarget.name ?? reissueTarget.runner_id,
              })}
            </Text>
            {reissueError !== null ? (
              <Alert color="red" title={t("runners.errorHeading")}>
                {reissueError}
              </Alert>
            ) : null}
            <Group justify="flex-end">
              <Button variant="default" onClick={cancelReissue}>
                {t("runners.reissue.cancelButton")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setReissueError(null);
                  reissueToken.mutate(reissueTarget.runner_id, {
                    onSuccess: (result) => {
                      setReissueTarget(null);
                      setShownToken(result.runner_token);
                    },
                    onError: (err) => {
                      setReissueError(runnerErrorText(err, t));
                    },
                  });
                }}
                loading={reissueToken.isPending}
                disabled={isRunnerNotFound(reissueToken.error)}
              >
                {t("runners.reissue.confirmButton")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>

      <RetireRunnerModal
        runner={retireTarget}
        onClose={() => {
          setRetireTarget(null);
        }}
      />

      <RunnerTokenModal
        token={shownToken}
        onClose={() => {
          setShownToken(null);
          // Токен — и в `shownToken`, и (до этого сброса) в `data`
          // завершённой мутации (`registerRunner`/`reissueToken`,
          // TanStack Query `MutationCache`). `reset()` снимает его
          // оттуда, не дожидаясь `gcTime`/размонтирования.
          registerRunner.reset();
          reissueToken.reset();
        }}
      />
    </Stack>
  );
}
